import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

type ShipmentType = "air" | "sea" | "land";

function NewConsolidadoPage() {
  const navigate = useNavigate();
  const createConsolidado = useMutation(
    api.companies.logistics.shipments.mutations.createShipment,
  );
  const configuration = useQuery(
    api.companies.logistics.configuration.queries.getActiveConfiguration,
  );

  const isCreatingRef = useRef(false);

  useEffect(() => {
    if (configuration === undefined || isCreatingRef.current) {
      return;
    }

    const createAndRedirect = async () => {
      isCreatingRef.current = true;

      try {
        const defaultShippingType =
          (configuration?.defaultCalendar?.shippingType as ShipmentType) ||
          "sea";

        const shipmentLabels: Record<ShipmentType, string> = {
          air: "Aereo",
          sea: "Maritimo",
          land: "Terrestre",
        };

        const departureDate =
          configuration?.nextDepartureInfo?.departureDate || "";
        const date = departureDate
          ? new Date(departureDate + "T12:00:00")
          : new Date();
        const monthNames = [
          "Ene",
          "Feb",
          "Mar",
          "Abr",
          "May",
          "Jun",
          "Jul",
          "Ago",
          "Sep",
          "Oct",
          "Nov",
          "Dic",
        ];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear().toString().slice(-2);
        const consolidadoName = `${shipmentLabels[defaultShippingType]} ${month}-${day}-${year}`;

        const consolidadoId = await createConsolidado({
          name: consolidadoName,
          shipmentType: defaultShippingType,
          shippingRouteId: configuration?.defaultShippingRouteId || undefined,
          carrierId: configuration?.defaultCarrierId || undefined,
          estimatedDepartureDate: departureDate
            ? new Date(departureDate + "T12:00:00").getTime()
            : undefined,
          estimatedArrivalDate: configuration?.nextDepartureInfo?.arrivalDate
            ? new Date(
                configuration.nextDepartureInfo.arrivalDate + "T12:00:00",
              ).getTime()
            : undefined,
        });

        navigate({
          to: "/logistics/containers/$id/edit",
          params: { id: consolidadoId },
          replace: true,
        });
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Error al crear consolidado",
        );
        isCreatingRef.current = false;
        navigate({ to: "/logistics/containers", replace: true });
      }
    };

    createAndRedirect();
  }, [configuration, createConsolidado, navigate]);

  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-muted-foreground">Creando nuevo consolidado...</p>
      </div>
    </div>
  );
}

export const Route = createFileRoute(
  "/_authenticated/logistics/containers/new",
)({
  component: NewConsolidadoPage,
});
